import { clearPreloadedGameWrappers } from '../../../utils/game/preloadedGame'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  activeGameModalContent,
  compilationScrollbarSettings,
  gameNotAvailableContent,
  gameRating,
  gamesList,
  inactiveGameModalContent,
  rating as ratingData,
  sept2026ChangesRatingData,
} from '../../../constants/copyright'
import { safeHTML } from '../../../utils/safeHTML'
import GamesItem from './gamesItem/GamesItem'
import { Scrollbar } from 'react-scrollbars-custom'
import { useContent } from '../../../redux/reducer/content'
import StatusPanel from '../../baseComponents/gui/statusPanel/StatusPanel'
import { useUser } from '../../../redux/reducer/user'
import { useRequests } from '../../../redux/reducer/requests'
import { useRequestsHandlerData } from '../../../hooks/useRequestsHandlerData'
import AppLoader from '../../features/appLoader/AppLoader'
import useStateControls from '../../../hooks/useStateControls'
import Toggle from '../../baseComponents/gui/toggle/Toggle'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import GamesRatingBox from './gamesRatingBox/GamesRatingBox'
import useServiceData from '../../../hooks/useServiceData'
import { GameWrapper } from '../../features/gameWrapper/GameWrapper'
import { useTranslation } from '../../../translations'
import Buffs from '../../features/buffs/main/Buffs'
import { useModalsDataActions } from '../../../hooks/useModalsDataActions'
import classNames from 'classnames'
import CustomBridge from '../../../controllers/bridge/CustomBridge'
import { useLoadGameController } from '../../../utils/scene/react/hooks/useLoadGameController'
import { showChangesRatingSept2026Modal } from '../../../utils/changesRatingSept2026'

const { title, list, toggle, requestTimeout: requestTimeoutValue } = gamesList

const Games = React.forwardRef(function ({ callbacks }, ref) {
  const {
    profile: { attempts },
  } = useUser()
  const {
    authData: { isWeb },
  } = useRequests()
  const {
    gamesSettings,
    gameRating: gameRatingGames,
    currentGameSeason,
    previousSeasonId,
    activeGameSection: section,
  } = useContent()

  const { addModal } = useModalsDataActions()
  const { setState } = useStateControls()
  const { get, save } = useServiceData()
  const { t } = useTranslation()

  const requestTimeout = useRef()
  const [isGameOpeningPending, setIsGameOpeningPending] = useState(false)

  const ratingGameSettings = useMemo(() => {
    const gameAllNames = Object.values(gamesList.list).reduce((acc, { isRating, apiName }) => {
      if (isRating) {
        acc.push(apiName)
      }

      return acc
    }, [])

    return gamesSettings?.filter(({ name }) => gameAllNames.includes(name))
  }, [gamesSettings])

  const gameModalId = useMemo(() => {
    if (['start', 'end'].some((date) => typeof currentGameSeason[date] !== 'number')) {
      return 'none'
    }

    return `isFirstGameEnter${currentGameSeason.start}${currentGameSeason.end}`
  }, [currentGameSeason])

  const gamesCallbacks = useMemo(
    () => ({
      games: () => section !== 'games' && callbacks.setActiveGameSection('games'),
      rating: () => section !== 'rating' && callbacks.setActiveGameSection('rating'),
    }),
    [section],
  )

  const toGameRating = (name) => {
    callbacks.setActiveGameRatingName(name)
    setState('gameRating')
  }

  // set для отслеживания игр, которые ждут вызова getOldGameRating после получения previousSeasonId
  const pendingOldRatings = useRef(new Set())

  // вызов getGameRating и добавление игры в очередь ожидания для последующего вызова getOldGameRating
  const getGameRatingWithOld = (name) => {
    callbacks?.getGameRating(name)
    pendingOldRatings.current.add(name)
  }

  const openGameModal = (item) => {
    if (item.timerId || isGameOpeningPending) {
      return
    }

    const { name, energy, isAvailable } = item

    const condition = attempts < energy

    if (!isAvailable) {
      addModal({
        type: 'inactiveGameModal',
        props: { ...gameNotAvailableContent, date: item.timeout },
      })

      return
    }

    addModal({
      type: condition ? 'inactiveGameModal' : 'startGameModal',

      props: condition
        ? {
            className: inactiveGameModalContent.className,
            img: list[name.toLowerCase()].image,
            title: inactiveGameModalContent.title,
            description: inactiveGameModalContent.text,
            buttons: inactiveGameModalContent.buttons,
            callbacks,
          }
        : {
            ...list[name.toLowerCase()],
            title: t(list[name.toLowerCase()].title),
            text: t(list[name.toLowerCase()].text),
            buttonText: activeGameModalContent.buttonText,
            callbacks,
          },
    })
  }

  const { loadGame, isPending: isLoadGameControllerPending } = useLoadGameController()

  const isPending = useRequestsHandlerData([
    { address: 'requestsGameGetGameSettings' },
    { address: 'requestsGameGetGameRating' },
    {
      address: 'requestsGameStart',
      pending: () => {
        setIsGameOpeningPending(true)
      },
      fulfilled: ({ log }) => {
        // NOTE: иногда стреляет ошибка в кибану, что log = undefined
        const { save_progress_interval, session } = log || {}
        const gameData = { interval: save_progress_interval, session }

        callbacks.setGameDataProgress(gameData)
        const thisGame = Object.values(list).find(({ apiName }) => log?.type === apiName)

        if (!thisGame) {
          setIsGameOpeningPending(false)

          return
        }

        callbacks.getAttempts()

        loadGame(thisGame.game)
      },
      rejected: () => {
        setIsGameOpeningPending(false)
        CustomBridge.instance.send({
          name: 'SHOW_NOTIFICATION',
          type: 0,
          data: {
            hasCloseButton: false,
            text: t('game.notification.loadingError'),
          },
        })
      },
    },
  ])

  const showAppLoader = isPending || isGameOpeningPending || isLoadGameControllerPending

  useEffect(() => {
    const sectionCallbacks = {
      rating: () => {
        if (typeof requestTimeout.current !== 'number' && ratingGameSettings?.length) {
          ratingGameSettings.forEach(({ name }) => {
            getGameRatingWithOld(name)
          })
          requestTimeout.current = setTimeout(
            () => (requestTimeout.current = null),
            requestTimeoutValue,
          )
        }
      },
      games: () => {
        const modalsData = get('modalsData') ?? {}

        if (!modalsData[gameModalId] && gameModalId !== 'none' && ratingGameSettings?.length) {
          ratingGameSettings.forEach(({ name }) => {
            getGameRatingWithOld(name)
          })
        }
      },
    }

    sectionCallbacks[section]?.()
  }, [section, gameModalId, ratingGameSettings])

  useEffect(() => {
    callbacks?.getGameSettings()
    clearPreloadedGameWrappers()
    callbacks?.getAttempts()
  }, [])

  useEffect(
    () => () => typeof requestTimeout.current === 'number' && clearTimeout(requestTimeout.current),
    [],
  )

  // отслеживание изменения previousSeasonId и вызов getOldGameRating для всех игр, которые ждут в pendingOldRatings
  useEffect(() => {
    if (previousSeasonId && pendingOldRatings.current.size > 0) {
      pendingOldRatings.current.forEach((name) => {
        callbacks?.getOldGameRating(name, previousSeasonId.toString())
      })
      pendingOldRatings.current.clear()
    }
  }, [previousSeasonId, callbacks])

  useEffect(() => {
    if (section === 'rating') {
      showChangesRatingSept2026Modal({
        modalServiceDataKey: sept2026ChangesRatingData.gameRating.serviceDataKey,
        state: 'gameRating',
        getServiceData: get,
        saveServiceData: save,
        addModal,
      })
    }
  }, [section])

  return (
    <GameWrapper>
      <section
        className={`games ${isWeb ? '' : 'app'}`}
        ref={ref}
      >
        <div className={'games__content'}>
          <div className={classNames('games__header', { 'games__header-reversed': true })}>
            <StatusPanel
              callbacks={callbacks}
              noLife={true}
              notClickable={false}
              type={'games'}
            />
            <h2 className={'games__title'}>{safeHTML(t(title))}</h2>
          </div>

          <Toggle
            {...toggle}
            funcs={gamesCallbacks}
            startActive={['games', 'rating'].indexOf(section)}
          />

          <Buffs className={'games__buffs'} />

          <SwitchTransition>
            <CSSTransition
              key={`section-${section}`}
              timeout={500}
              classNames={'games__main'}
            >
              <div className={'games__main'}>
                {{
                  games: () => {
                    return (
                      <div className={'games__list'}>
                        <Scrollbar {...compilationScrollbarSettings}>
                          {gamesSettings?.map((item, index) => {
                            const userRating = gameRatingGames?.[`user${item.name}`]

                            return (
                              <GamesItem
                                key={`game-${index}`}
                                {...item}
                                index={index}
                                section={section}
                                rating={userRating?.position}
                                gameData={list[item.name.toLowerCase()]}
                                onClick={() => openGameModal(item)}
                              />
                            )
                          })}
                        </Scrollbar>
                      </div>
                    )
                  },
                  rating: () => {
                    return (
                      <div className={'games__rating'}>
                        <Scrollbar {...compilationScrollbarSettings}>
                          <GamesRatingBox
                            {...gameRating.box}
                            seasonDates={currentGameSeason}
                            date={t(ratingData.date.week.new)}
                            text={t('boxesTextFt.ratingBoxText')}
                          />
                          {/* <GamesRatingBox
                            {...gameRating.box}
                            date={t(gameRating.box.date)}
                            text={t(gameRating.box.text)}
                            button={{
                              ...gameRating.box.button,
                              text: t(gameRating.box.button.text)
                            }}
                            seasonDates={currentGameSeason}
                          /> */}

                          {ratingGameSettings?.map((item, index) => {
                            const currentRating = gameRatingGames?.[item.name]
                            const userRating = gameRatingGames?.[`user${item.name}`]

                            return (
                              currentRating && (
                                <GamesItem
                                  key={`game-${index}`}
                                  {...item}
                                  index={index}
                                  gameData={list[item.name.toLowerCase()]}
                                  rating={userRating?.position}
                                  energy={null}
                                  section={section}
                                  onClick={() => toGameRating(item.name)}
                                />
                              )
                            )
                          })}
                        </Scrollbar>
                      </div>
                    )
                  },
                }[section]?.()}
              </div>
            </CSSTransition>
          </SwitchTransition>
        </div>
        {showAppLoader && <AppLoader />}
      </section>
    </GameWrapper>
  )
})

export default Games
Games.propTypes = {}
